import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, Delete, Star, StarBorder } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../CustomDatePicker';
import {
  createEmptyCondition,
  getOperatorsForColumn,
  getValidConditions,
  isDateColumn,
  normalizeConditions,
  operatorNeedsValue
} from './filterEngine';

const TaskFilterDialog = ({
  open,
  anchorEl,
  onClose,
  onApply,
  onSave,
  conditions = [],
  columns = [],
  availableStatuses = []
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState([createEmptyCondition()]);
  const [filterName, setFilterName] = useState('');
  const [saveAsFavorite, setSaveAsFavorite] = useState(true);
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = normalizeConditions(conditions);
    setDraft(next.length > 0 ? next : [createEmptyCondition()]);
    setFilterName('');
    setSaveAsFavorite(true);
    setNameError('');
    setSaving(false);
  }, [open, conditions]);

  const updateRow = (id, field, value) => {
    setDraft((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      if (field === 'column') {
        const nextOperator = getOperatorsForColumn(value)[0];
        return { ...row, column: value, operator: nextOperator, value: '' };
      }
      if (field === 'operator') {
        return {
          ...row,
          operator: value,
          value: operatorNeedsValue(value) ? row.value : ''
        };
      }
      return { ...row, [field]: value };
    }));
  };

  const addRow = () => {
    setDraft((prev) => [...prev, createEmptyCondition()]);
  };

  const removeRow = (id) => {
    setDraft((prev) => (prev.length <= 1 ? [createEmptyCondition()] : prev.filter((row) => row.id !== id)));
  };

  const handleApply = () => {
    onApply?.(getValidConditions(draft));
    onClose?.();
  };

  const handleSave = async () => {
    const valid = getValidConditions(draft);
    const name = filterName.trim();
    if (!name) {
      setNameError(t('taskFilters.nameRequired'));
      return;
    }
    if (valid.length === 0) {
      setNameError(t('taskFilters.noConditions'));
      return;
    }

    setNameError('');
    setSaving(true);
    try {
      const saved = await onSave?.({
        name,
        conditions: valid,
        setAsDefault: saveAsFavorite
      });
      if (saved === false) return;
      onApply?.(valid);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const compactFieldSx = { minWidth: 140, flex: 1 };

  const renderValueInput = (row) => {
    if (!operatorNeedsValue(row.operator)) {
      return null;
    }

    if (isDateColumn(row.column)) {
      return (
        <CustomDatePicker
          label={t('taskFilters.value')}
          value={row.value || ''}
          onChange={(event) => updateRow(row.id, 'value', event.target.value)}
          sx={{ minWidth: 150, flex: 1 }}
        />
      );
    }

    if (row.column === 'report_status') {
      return (
        <FormControl size="small" sx={compactFieldSx}>
          <InputLabel>{t('taskFilters.value')}</InputLabel>
          <Select
            value={row.value}
            label={t('taskFilters.value')}
            onChange={(event) => updateRow(row.id, 'value', event.target.value)}
          >
            {availableStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {t(`reportStatus.${status}`, status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        size="small"
        label={t('taskFilters.value')}
        value={row.value}
        onChange={(event) => updateRow(row.id, 'value', event.target.value)}
        sx={compactFieldSx}
      />
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            width: 520,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            overflow: 'hidden'
          }
        }
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, mb: 1.25 }}>
          {t('taskFilters.dialogTitle')}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            mb: 0.5
          }}
        >
          <TextField
            size="small"
            label={t('taskFilters.filterName')}
            value={filterName}
            onChange={(event) => {
              setFilterName(event.target.value);
              if (nameError) setNameError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleSave();
              }
            }}
            error={Boolean(nameError)}
            helperText={nameError || t('taskFilters.filterNameHint')}
            FormHelperTextProps={{
              sx: { mx: 0, mt: 0.5, lineHeight: 1.35 }
            }}
            sx={{ minWidth: 180, flex: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                icon={<StarBorder />}
                checkedIcon={<Star />}
                checked={saveAsFavorite}
                onChange={(event) => setSaveAsFavorite(event.target.checked)}
                sx={{ color: 'orange', '&.Mui-checked': { color: 'orange' }, mt: -0.25 }}
              />
            }
            label={t('taskFilters.saveAsFavorite')}
            sx={{ mr: 0, mt: 0.5, whiteSpace: 'nowrap' }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {t('taskFilters.conditionsSection')}
        </Typography>

        {draft.map((row) => (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              gap: 1,
              mb: 1.25,
              alignItems: 'center',
              flexWrap: 'nowrap'
            }}
          >
            <FormControl size="small" sx={compactFieldSx}>
              <InputLabel>{t('taskFilters.column')}</InputLabel>
              <Select
                value={row.column}
                label={t('taskFilters.column')}
                onChange={(event) => updateRow(row.id, 'column', event.target.value)}
              >
                {columns.map((column) => (
                  <MenuItem key={column.field} value={column.field}>
                    {column.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
              <InputLabel>{t('taskFilters.operator')}</InputLabel>
              <Select
                value={row.operator}
                label={t('taskFilters.operator')}
                onChange={(event) => updateRow(row.id, 'operator', event.target.value)}
              >
                {getOperatorsForColumn(row.column).map((operator) => (
                  <MenuItem key={operator} value={operator}>
                    {t(`taskFilters.operators.${operator}`, operator)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {renderValueInput(row)}

            <IconButton
              size="small"
              onClick={() => removeRow(row.id)}
              disabled={draft.length === 1}
              sx={{ color: 'error.main', flexShrink: 0 }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.25, mb: 0.5 }}>
          <Tooltip title={t('taskFilters.addCondition')}>
            <IconButton onClick={addRow} size="small" sx={{ color: 'var(--rd-primary)' }}>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.grey[800] : 'var(--rd-bg-dialog-actions)',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Button
          onClick={handleSave}
          variant="contained"
          className="button"
          disabled={saving}
          size="small"
        >
          {t('common.save')}
        </Button>
        <Button
          onClick={handleApply}
          variant="outlined"
          size="small"
        >
          {t('common.apply')}
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          className="button-cancel"
          size="small"
        >
          {t('common.cancel')}
        </Button>
      </Box>
    </Popover>
  );
};

export default TaskFilterDialog;
